/**
 * API Test Component
 * Test component to verify React Query and httpOnly cookie authentication
 */

import React, { useState } from 'react';
import { useAuth, useUserPermissions } from '../hooks/useAuth';
import { useStudents } from '../hooks/useStudents';
import { Button } from './ui/button';
import { Card } from './ui/card';

const ApiTest = () => {
  const [testCredentials] = useState({
    username_or_email: 'admin',
    password: 'admin123'
  });

  const {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    isLoggingIn,
    isLoggingOut,
    loginError,
  } = useAuth();

  const { permissions, userType, isAdmin } = useUserPermissions();

  const {
    data: studentsData,
    isLoading: isLoadingStudents,
    error: studentsError,
    refetch: refetchStudents,
  } = useStudents({}, { 
    enabled: isAuthenticated 
  });

  const handleLogin = () => {
    login(testCredentials);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-6">
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-4">
          🧪 API & Authentication Test
        </h2>
        
        {/* Authentication Status */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Authentication Status</h3>
          <div className="space-y-2">
            <p>
              <strong>Status:</strong>{' '}
              <span className={isAuthenticated ? 'text-green-600' : 'text-red-600'}>
                {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
              </span>
            </p>
            {isLoading && <p>🔄 Loading user...</p>}
            {user && (
              <div>
                <p><strong>User:</strong> {user.username} ({user.email})</p>
                <p><strong>Type:</strong> {userType}</p>
                <p><strong>Admin:</strong> {isAdmin ? 'Yes' : 'No'}</p>
                <p><strong>Permissions:</strong> {permissions.length} permissions loaded</p>
              </div>
            )}
            {loginError && (
              <p className="text-red-600">
                <strong>Login Error:</strong> {loginError.message}
              </p>
            )}
          </div>
        </div>

        {/* Authentication Controls */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Authentication Controls</h3>
          <div className="flex gap-2">
            {!isAuthenticated ? (
              <Button 
                onClick={handleLogin} 
                disabled={isLoggingIn}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoggingIn ? '🔄 Logging in...' : '🔑 Login as Test Admin'}
              </Button>
            ) : (
              <Button 
                onClick={handleLogout} 
                disabled={isLoggingOut}
                variant="destructive"
              >
                {isLoggingOut ? '🔄 Logging out...' : '🚪 Logout'}
              </Button>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Test credentials: admin / admin123 (backend admin user)
          </p>
        </div>

        {/* API Data Test */}
        {isAuthenticated && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">API Data Test</h3>
            <div className="space-y-2">
              {isLoadingStudents ? (
                <p>🔄 Loading students data...</p>
              ) : studentsError ? (
                <div>
                  <p className="text-red-600">
                    <strong>API Error:</strong> {studentsError.message}
                  </p>
                  <Button 
                    onClick={refetchStudents} 
                    size="sm" 
                    variant="outline"
                    className="mt-2"
                  >
                    🔄 Retry
                  </Button>
                </div>
              ) : studentsData ? (
                <div>
                  <p className="text-green-600">
                    ✅ Students API working! Found {studentsData.count || studentsData.results?.length || 0} students
                  </p>
                  {studentsData.results && studentsData.results.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Sample student:</p>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify(studentsData.results[0], null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <p>No students data available</p>
              )}
            </div>
          </div>
        )}

        {/* Technical Details */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Technical Details</h3>
          <div className="text-sm space-y-1 bg-gray-50 p-3 rounded">
            <p><strong>Authentication:</strong> httpOnly cookies (secure, XSS-protected)</p>
            <p><strong>State Management:</strong> React Query (automatic caching & background updates)</p>
            <p><strong>API Base URL:</strong> {process.env.REACT_APP_API_URL || 'http://localhost:8000'}</p>
            <p><strong>CORS:</strong> Credentials included for session cookies</p>
            <p><strong>CSRF Protection:</strong> Automatic token retrieval and validation</p>
          </div>
        </div>

        {/* Connection Status */}
        <div className="text-center">
          <div className={`inline-block px-3 py-1 rounded-full text-sm ${
            isAuthenticated 
              ? 'bg-green-100 text-green-800' 
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {isAuthenticated ? '🔒 Secure Connection Active' : '🔓 Not Connected'}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ApiTest;